import { ButtonError, ButtonLight, ButtonPrimary } from 'components/Button'
import { AutoColumn } from 'components/Column'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import { RowBetween } from 'components/Row'
import { DynamicSection } from 'pages/AddLiquidity/styled'
import React from 'react'
import { TYPE } from 'theme'
import { Text } from 'rebass'
import { Field } from 'state/mint/actions'
import { ApprovalState } from 'hooks/useApproveCallback'
import { Dots } from 'pages/Pool/styleds'
import { TokenAmount } from '@uniswap/sdk-core'

interface Step3Props {
  activeStep: number
  tickLower?: number
  tickUpper?: number
  invalidPool: boolean
  invalidRange: boolean
  hasExistingPosition: boolean
  formattedAmounts: Record<string, string>
  maxAmounts: Record<string, any>
  atMaxAmounts: { CURRENCY_A?: TokenAmount | undefined; CURRENCY_B?: TokenAmount | undefined }
  currencies: Record<string, any>
  usdcValues: Record<string, any>
  onFieldAInput: (val: string) => void
  onFieldBInput: (val: string) => void
  depositADisabled: boolean
  depositBDisabled: boolean
  addIsUnsupported: boolean
  account?: string | null
  toggleWalletModal: () => void
  approvalA: ApprovalState
  approvalB: ApprovalState
  approveACallback: () => void
  approveBCallback: () => void
  showApprovalA: boolean
  showApprovalB: boolean
  isValid: boolean
  parsedAmounts: Record<string, any>
  expertMode: boolean
  onAdd: () => void
  setShowConfirm: (val: boolean) => void
  attemptingTxn: boolean
  errorMessage: string | undefined
}
const Step3: React.FC<Step3Props> = ({
  activeStep,
  tickLower,
  tickUpper,
  invalidPool,
  invalidRange,
  hasExistingPosition,
  formattedAmounts,
  maxAmounts,
  atMaxAmounts,
  currencies,
  usdcValues,
  onFieldAInput,
  onFieldBInput,
  depositADisabled,
  depositBDisabled,
  addIsUnsupported,
  account,
  toggleWalletModal,
  approvalA,
  approvalB,
  approveACallback,
  approveBCallback,
  showApprovalA,
  showApprovalB,
  isValid,
  parsedAmounts,
  expertMode,
  onAdd,
  setShowConfirm,
  attemptingTxn,
  errorMessage,
}) => {
  const getButtonLabel = () => {
    const approvalLoading =
      approvalA === ApprovalState.PENDING ||
      approvalB === ApprovalState.PENDING ||
      approvalA === ApprovalState.UNKNOWN ||
      approvalB === ApprovalState.UNKNOWN

    if (errorMessage) {
      return errorMessage
    } else if (
      approvalLoading &&
      parsedAmounts[Field.CURRENCY_A] !== undefined &&
      parsedAmounts[Field.CURRENCY_B] !== undefined
    ) {
      return <Dots>Checking allowance</Dots>
    } else if (attemptingTxn) {
      return <Dots>Loading</Dots>
    } else {
      return 'Add'
    }
  }

  return (
    <>
      {activeStep === 3 && (
        <>
          <DynamicSection disabled={tickLower === undefined || tickUpper === undefined || invalidPool || invalidRange}>
            <AutoColumn gap="md">
              <TYPE.label>{hasExistingPosition ? 'Add more liquidity' : 'Deposit Amounts'}</TYPE.label>

              <CurrencyInputPanel
                value={formattedAmounts[Field.CURRENCY_A]}
                onUserInput={onFieldAInput}
                onMax={() => {
                  onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
                }}
                showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
                currency={currencies[Field.CURRENCY_A]}
                id="add-liquidity-input-tokena"
                fiatValue={usdcValues[Field.CURRENCY_A]}
                showCommonBases
                locked={depositADisabled}
              />

              <CurrencyInputPanel
                value={formattedAmounts[Field.CURRENCY_B]}
                onUserInput={onFieldBInput}
                onMax={() => {
                  onFieldBInput(maxAmounts[Field.CURRENCY_B]?.toExact() ?? '')
                }}
                showMaxButton={!atMaxAmounts[Field.CURRENCY_B]}
                fiatValue={usdcValues[Field.CURRENCY_B]}
                currency={currencies[Field.CURRENCY_B]}
                id="add-liquidity-input-tokenb"
                showCommonBases
                locked={depositBDisabled}
              />
            </AutoColumn>
          </DynamicSection>
          <div>
            {addIsUnsupported ? (
              <ButtonPrimary disabled={true} borderRadius="12px" padding={'12px'}>
                <TYPE.main mb="4px">Unsupported Asset</TYPE.main>
              </ButtonPrimary>
            ) : !account ? (
              <ButtonLight onClick={toggleWalletModal} borderRadius="12px" padding={'12px'}>
                Connect Wallet
              </ButtonLight>
            ) : (
              <AutoColumn gap={'md'}>
                {(approvalA === ApprovalState.NOT_APPROVED ||
                  approvalA === ApprovalState.PENDING ||
                  approvalB === ApprovalState.NOT_APPROVED ||
                  approvalB === ApprovalState.PENDING) &&
                  isValid && (
                    <RowBetween>
                      {showApprovalA && (
                        <ButtonPrimary
                          borderRadius="12px"
                          padding={'12px'}
                          onClick={approveACallback}
                          disabled={approvalA === ApprovalState.PENDING}
                          width={showApprovalB ? '48%' : '100%'}
                        >
                          {approvalA === ApprovalState.PENDING ? (
                            <Dots>Approving {currencies[Field.CURRENCY_A]?.symbol}</Dots>
                          ) : (
                            'Approve ' + currencies[Field.CURRENCY_A]?.symbol
                          )}
                        </ButtonPrimary>
                      )}
                      {showApprovalB && (
                        <ButtonPrimary
                          borderRadius="12px"
                          padding={'12px'}
                          onClick={approveBCallback}
                          disabled={approvalB === ApprovalState.PENDING}
                          width={showApprovalA ? '48%' : '100%'}
                        >
                          {approvalB === ApprovalState.PENDING ? (
                            <Dots>Approving {currencies[Field.CURRENCY_B]?.symbol}</Dots>
                          ) : (
                            'Approve ' + currencies[Field.CURRENCY_B]?.symbol
                          )}
                        </ButtonPrimary>
                      )}
                    </RowBetween>
                  )}
                <ButtonError
                  onClick={() => {
                    expertMode ? onAdd() : setShowConfirm(true)
                  }}
                  disabled={
                    !isValid ||
                    (approvalA !== ApprovalState.APPROVED && !depositADisabled) ||
                    (approvalB !== ApprovalState.APPROVED && !depositBDisabled) ||
                    attemptingTxn === true
                  }
                  error={!isValid && !!parsedAmounts[Field.CURRENCY_A] && !!parsedAmounts[Field.CURRENCY_B]}
                >
                  <Text fontWeight={500}>{getButtonLabel()}</Text>
                </ButtonError>
              </AutoColumn>
            )}
          </div>
        </>
      )}
    </>
  )
}
export default React.memo(Step3)
